import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../context/user.context";
import axiosApi from "../../api/axiosInstance";
import { toast } from "react-toastify";
import {
  UilCheck,
  UilTruck,
  UilClock,
  UilSync,
} from "@iconscout/react-unicons";
// ⚠️ Verify UilCheck / UilTruck / UilClock actually exist in
// @iconscout/react-unicons in this project — swap for whatever's
// actually available if any of these throw an import error.

// Matches the backend's Friday-off rule -- 5 = Friday (Date.getDay()).
const OFF_DAY = 5;

const STATUS_META = {
  DELIVERED: { label: "Delivered", Icon: UilCheck, tone: "text-green-600" },
  Scheduled: { label: "Arriving soon", Icon: UilTruck, tone: "text-secondary" },
  PENDING: { label: "Pending", Icon: UilClock, tone: "text-gray-400" },
};

/**
 * Whether a PENDING batch can still be rescheduled from the app.
 * Anything already Scheduled (sent to Sonic by the daily dispatch job,
 * which runs ~24h before ScheduledDate) or DELIVERED can't be touched
 * here -- matches the same 24h cutoff the backend enforces.
 */
function isReschedulable(batch) {
  if (batch.status !== "PENDING") return false;
  const hoursUntil =
    (new Date(batch.scheduledDate).getTime() - Date.now()) / (1000 * 60 * 60);
  return hoursUntil > 24;
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

// Same convention as Orders.jsx's formatOrderNumber -- last 6 chars,
// uppercased, for a full 36-char order reference GUID.
function formatOrderNumber(orderNumber) {
  if (orderNumber?.length === 36) {
    return orderNumber.slice(-6).toUpperCase();
  }
  return orderNumber;
}

export default function PartialDeliveries() {
  const { currentUser } = useContext(UserContext);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // { planId, batchId, currentDate, takenDates }
  const [newDate, setNewDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    LoadData();
    // eslint-disable-next-line
  }, [currentUser?.customerCode]);

  async function LoadData() {
    if (!currentUser?.customerCode) return;
    setLoading(true);
    setError(null);
    try {
      // Combined call -- backend returns each plan with its batches
      // nested, so this page never needs a per-plan follow-up request.
      const response = await axiosApi.get("/api/v2/delivery/plans");
      setPlans(response.data?.data || []);
    } catch (err) {
      setError(err.message || "Failed to load your deliveries");
    } finally {
      setLoading(false);
    }
  }

  function openReschedule(plan, batch) {
    const takenDates = (plan.batches || [])
      .filter((b) => b.batchId !== batch.batchId)
      .map((b) => b.scheduledDate.slice(0, 10));

    setRescheduleTarget({
      planId: plan.planId,
      batchId: batch.batchId,
      currentDate: batch.scheduledDate.slice(0, 10),
      takenDates,
    });
    setNewDate("");
  }

  function closeReschedule() {
    setRescheduleTarget(null);
    setNewDate("");
  }

  async function confirmReschedule() {
    if (!newDate) {
      toast.error("Pick a date first");
      return;
    }

    const picked = new Date(newDate);
    if (picked.getDay() === OFF_DAY) {
      toast.error("Friday isn't a delivery day -- pick another date");
      return;
    }
    if (rescheduleTarget.takenDates.includes(newDate)) {
      toast.error("You already have a delivery on that date");
      return;
    }

    //console.log("Rescheduling batch", rescheduleTarget.batchId, "to", newDate);

    try {
      setSubmitting(true);
      await axiosApi.patch(
        `/api/v2/delivery/batches/${rescheduleTarget.batchId}/reschedule`,
        { newDate }
      );

      toast.success("Delivery rescheduled");

      setPlans((prev) =>
        prev.map((plan) =>
          plan.planId !== rescheduleTarget.planId
            ? plan
            : {
                ...plan,
                batches: plan.batches.map((b) =>
                  b.batchId !== rescheduleTarget.batchId
                    ? b
                    : { ...b, scheduledDate: newDate }
                ),
              }
        )
      );

      closeReschedule();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        "Couldn't reschedule that delivery. Try a different date.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="text-navarbarText text-3xl font-poppins font-medium mb-8">
          My Deliveries
          <p className="text-base text-darkTextColor font-poppins font-normal">
            Upcoming and past partial deliveries for your orders.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            className="flex rounded-md bg-secondary h-10 w-10 items-center justify-center text-center"
            onClick={LoadData}
            disabled={loading}
          >
            <UilSync className="w-6 text-textWhite" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-5 text-center font-poppins">Loading...</div>
      ) : error ? (
        <div className="py-5 text-center text-red-600 font-poppins">
          {error}
        </div>
      ) : plans.length === 0 ? (
        <div className="py-5 text-center font-poppins text-darkTextColor">
          No deliveries to display
        </div>
      ) : (
        <div className="mt-8 flex flex-col gap-6">
          {plans.map((plan) => {
            const batches = plan.batches || [];
            const delivered = batches.filter(
              (b) => b.status === "DELIVERED"
            ).length;
            const total = batches.length;
            const progressPct =
              total > 0 ? Math.round((delivered / total) * 100) : 0;

            return (
              <div
                key={plan.planId}
                className="bg-white border border-gray-200 rounded-md p-4"
              >
                <div className="bg-secondaryVeryLight rounded-md p-1 mb-2">
                  <div className="flex items-center justify-between mb-0.5 ">
                    <p className="text-sm font-poppins text-gray-500">
                      Order #{formatOrderNumber(plan.orderReference)}
                    </p>
                    <p className="text-sm font-poppins text-gray-500">
                      {new Date(plan.purchaseDate).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                  <p className="text-sm font-poppins font-medium text-gray-900 mb-2.5">
                    {delivered} of {total} deliveries delivered
                  </p>
                  <div className="h-1.5 bg-gray-100 rounded overflow-hidden mb-4">
                    <div
                      className="h-full bg-secondary transition-all duration-300"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {batches.map((batch) => {
                    const meta =
                      STATUS_META[batch.status] || STATUS_META.PENDING;
                    const canReschedule = isReschedulable(batch);
                    const StatusIcon = meta.Icon;

                    return (
                      <div
                        key={batch.batchId}
                        className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-md px-3 py-2.5"
                      >
                        <span className={`flex ${meta.tone}`}>
                          <StatusIcon size="20" />
                        </span>
                        <div className="flex-1">
                          <p className="text-sm font-poppins font-medium text-gray-900 m-0">
                            {formatDate(batch.scheduledDate)} · {batch.quantity}{" "}
                            bottles
                          </p>
                          <p
                            className={`text-xs font-poppins m-0 ${meta.tone}`}
                          >
                            {meta.label}
                          </p>
                        </div>
                        {canReschedule ? (
                          <button
                            onClick={() => openReschedule(plan, batch)}
                            className="flex bg-secondary h-8 rounded-md px-3 text-textWhite justify-center text-center items-center text-sm font-poppins hover:bg-secondaryHover"
                          >
                            Reschedule
                          </button>
                        ) : batch.status !== "DELIVERED" ? (
                          <span className="text-xs font-poppins text-gray-400 text-right">
                            Contact Call Center to change
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {rescheduleTarget && (
        <div
          className="fixed inset-0 bg-black/45 flex items-center justify-center z-[1000] p-4"
          onClick={closeReschedule}
        >
          <div
            className="bg-primaryVeryLight rounded-md p-5 max-w-[340px] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-base font-poppins font-medium text-gray-900 mb-2">
              Reschedule delivery
            </h2>
            <p className="text-sm font-poppins text-gray-500 mb-4">
              Pick a new date. Fridays aren't available, and it can't clash with
              another delivery in this order.
            </p>
            <input
              type="date"
              value={newDate}
              min={new Date(Date.now() + 24 * 60 * 60 * 1000)
                .toISOString()
                .slice(0, 10)}
              onChange={(e) => setNewDate(e.target.value)}
              className="w-full px-2.5 py-2 border border-gray-300 rounded-md text-sm font-poppins mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={closeReschedule}
                disabled={submitting}
                className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-poppins disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmReschedule}
                disabled={submitting}
                className="px-4 py-2 rounded-md bg-secondary text-textWhite text-sm font-poppins hover:bg-secondaryHover disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Saving..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
